import React from 'react';
import { Table } from '@innovationnorway/component-library';
import { Button } from '@innovationnorway/cl-react';
import css from './table.module.scss';
import cn from 'classnames';
import TogglableEditCell from './TogglableEditCell';
import { SivaInnovationFundsDto } from 'src/api/v2';
import { useForm } from 'react-hook-form';
import { UseMutationResult } from '@tanstack/react-query';
import { formatCurrencySimple } from 'src/utils/FormatValue';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'src/features/authorization';

type Props = {
  editMode: boolean;
  clusterEsaData: SivaInnovationFundsDto | undefined;
  upsert?: UseMutationResult<SivaInnovationFundsDto, unknown, SivaInnovationFundsDto, unknown>;
  deactivate?: () => void;
  sumOther: number | null;
  sumPreInc: number | null;
  sumAdministration: number | null;
  year: string | undefined;
  hidePreInc?: boolean;
  hideOtherPhases?: boolean;
  hideAdministration?: boolean;
};

type CellProps = {
  value: keyof SivaInnovationFundsDto;
};

const FundsTable = ({
  editMode,
  clusterEsaData,
  upsert,
  deactivate,
  year,
  sumPreInc,
  sumOther,
  sumAdministration,
  hidePreInc = false,
  hideOtherPhases = false,
  hideAdministration = false,
}: Props) => {
  const { t: tSiva } = useTranslation('siva');
  const { checkAccess } = useAuth();
  const isBusinessGarden = checkAccess('UI_SivaBusinessGarden').read;

  const form = useForm<SivaInnovationFundsDto>({
    defaultValues: {
      ...clusterEsaData,
    },
  });

  const handleSubmit = form.handleSubmit((data) => {
    if (upsert && deactivate) return upsert.mutateAsync(data).then(() => deactivate());
  });

  const Cell = ({ value }: CellProps) => {
    return (
      <TogglableEditCell form={form} clusterEsaData={clusterEsaData} value={value} editMode={editMode} />
    );
  };

  return (
    <>
      <form onSubmit={handleSubmit}>
        <Table
          className="width--100"
          id="tildelteMidler"
          caption={tSiva('esareport.details.overviewAssignedFunds')}
          hideCaption
          rows={[
            ...(!isBusinessGarden && !hidePreInc
              ? [
                  {
                    className: css['table__group'],
                    rows: [
                      {
                        cells: [
                          tSiva('esareport.details.transferredFromLastYear'),
                          <Cell key={'preIncTransferred'} value={'preIncTransferred'} />,
                        ],
                        className: css['table-row'],
                      },
                      {
                        cells: [
                          tSiva('esareport.details.ordinary'),
                          <Cell key={'preIncOrdinary'} value="preIncOrdinary" />,
                        ],
                        className: css['table-row'],
                      },
                      {
                        cells: [
                          tSiva('esareport.details.topupFunding'),
                          <Cell key={'preIncTop'} value="preIncTop" />,
                        ],
                        className: css['table-row'],
                      },

                      {
                        cells: [
                          tSiva('esareport.details.other'),
                          <Cell key={'preIncOther'} value="preIncOther" />,
                        ],
                        className: css['table-row'],
                      },
                      {
                        cells: [
                          tSiva('esareport.details.sumDisposable', { year }),
                          editMode ? '' : sumPreInc ? formatCurrencySimple(sumPreInc) : '',
                        ],
                        className: cn(css['table-row'], css['last-row']),
                      },
                    ],
                    subHeading: tSiva('esareport.details.phase.preincubation'),
                  },
                ]
              : []),
            ...(!hideOtherPhases
              ? [
                  {
                    className: css['table__group'],
                    rows: [
                      {
                        cells: [
                          tSiva('esareport.details.transferredFromLastYear'),
                          <Cell key={'transferred'} value="transferred" />,
                        ],
                        className: css['table-row'],
                      },
                      {
                        cells: [
                          tSiva('esareport.details.ordinary'),
                          <Cell key={'ordinary'} value="ordinary" />,
                        ],
                        className: css['table-row'],
                      },
                      {
                        cells: [tSiva('esareport.details.topupFunding'), <Cell key={'top'} value="top" />],
                        className: css['table-row'],
                      },
                      {
                        cells: [tSiva('esareport.details.other'), <Cell key={'other'} value="other" />],
                        className: css['table-row'],
                      },
                      {
                        cells: [
                          tSiva('esareport.details.sumDisposable', { year }),
                          editMode ? '' : sumOther ? formatCurrencySimple(sumOther) : '',
                        ],
                        className: cn(css['table-row'], css['last-row']),
                      },
                    ],
                    subHeading: isBusinessGarden
                      ? tSiva('esareport.details.phase.funds')
                      : tSiva('esareport.details.phase.other'),
                  },
                ]
              : []),
            ...(!hideAdministration
              ? [
                  {
                    className: css['table__group'],
                    rows: [
                      {
                        cells: [
                          tSiva('esareport.details.ordinaryAdmin'),
                          <Cell key={'ordinary'} value="ordinaryOperationAndAdministration" />,
                        ],
                        className: css['table-row'],
                      },
                      {
                        cells: [
                          tSiva('esareport.details.topupFundingAdmin'),
                          <Cell key={'top'} value="topOperationAndAdministration" />,
                        ],
                        className: css['table-row'],
                      },
                      {
                        cells: [
                          tSiva('esareport.details.otherAdmin'),
                          <Cell key={'other'} value="otherOperationAndAdministration" />,
                        ],
                        className: css['table-row'],
                      },
                      {
                        cells: [
                          tSiva('esareport.details.sumDisposableAdmin', { year }),
                          editMode ? '' : sumAdministration ? formatCurrencySimple(sumAdministration) : '',
                        ],
                        className: cn(css['table-row'], css['last-row']),
                      },
                    ],
                    subHeading: tSiva('esareport.details.adminstration'),
                  },
                ]
              : []),
          ]}
        />
        {editMode && (
          <div className="display--flex margin-top--6 gap--2">
            <Button type="submit">Lagre</Button>
            <Button type="button" variant="secondary" onClick={deactivate}>
              Avbryt
            </Button>
          </div>
        )}
      </form>
    </>
  );
};

export default FundsTable;
